import React, { useState, useEffect } from 'react';
import i18n from 'i18next';
import { FiBook, FiX, FiHome, FiUsers, FiDatabase, FiShoppingCart, FiFileText, FiFilter, FiInfo } from 'react-icons/fi';
import { LuMicroscope } from 'react-icons/lu';
import './HelpModal.css';

const HelpModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);

    // Esc closes — same behaviour as the document viewer.
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen]);

    return (
        <>
            <button
                type="button"
                className="nav-item"
                onClick={openModal}
                title={i18n.t('help_button')}
            >
                <FiBook size={18} />
                <span>{i18n.t('help_button')}</span>
            </button>

            {isOpen && (
                <div className="help-modal-overlay" onClick={closeModal}>
                    <div
                        className="help-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="help-modal-title"
                    >
                        <div className="help-modal-header">
                            <h2 id="help-modal-title">{i18n.t('help_title')}</h2>
                            <button
                                className="help-modal-close"
                                onClick={closeModal}
                                aria-label={i18n.t('close')}
                            >
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className="help-modal-tabs">
                            <button
                                className={`help-tab ${activeTab === 'overview' ? 'active' : ''}`}
                                onClick={() => setActiveTab('overview')}
                            >
                                <FiInfo size={16} />
                                {i18n.t('help_overview_tab')}
                            </button>
                            <button
                                className={`help-tab ${activeTab === 'navigation' ? 'active' : ''}`}
                                onClick={() => setActiveTab('navigation')}
                            >
                                <FiHome size={16} />
                                {i18n.t('help_navigation_tab')}
                            </button>
                            <button
                                className={`help-tab ${activeTab === 'filters' ? 'active' : ''}`}
                                onClick={() => setActiveTab('filters')}
                            >
                                <FiFilter size={16} />
                                {i18n.t('help_filters_tab')}
                            </button>
                        </div>

                        <div className="help-modal-content">
                            {/* keyed wrapper — remounts on tab switch so the
                                CSS keyframe `routeIn` runs on each change */}
                            <div className="help-tab-pane" key={activeTab}>
                            {activeTab === 'overview' && (
                                <div className="help-section">
                                    <h3>{i18n.t('help_overview_title')}</h3>
                                    <p>{i18n.t('help_overview_intro')}</p>

                                    <h4>{i18n.t('help_overview_features_title')}</h4>
                                    <ul>
                                        <li>{i18n.t('help_overview_feature_1')}</li>
                                        <li>{i18n.t('help_overview_feature_2')}</li>
                                        <li>{i18n.t('help_overview_feature_3')}</li>
                                        <li>{i18n.t('help_overview_feature_4')}</li>
                                    </ul>

                                    <h4>{i18n.t('help_overview_start_title')}</h4>
                                    <p>{i18n.t('help_overview_start_desc')}</p>

                                    <h4>{i18n.t('help_workflow_title')}</h4>
                                    <ul>
                                        <li><strong>1.</strong> {i18n.t('help_workflow_step1')}</li>
                                        <li><strong>2.</strong> {i18n.t('help_workflow_step2')}</li>
                                        <li><strong>3.</strong> {i18n.t('help_workflow_step3')}</li>
                                        <li><strong>4.</strong> {i18n.t('help_workflow_step4')}</li>
                                    </ul>
                                </div>
                            )}

                            {activeTab === 'navigation' && (
                                <div className="help-section">
                                    <h3>{i18n.t('help_navigation_title')}</h3>

                                    <div className="help-nav-item">
                                        <div className="help-nav-icon"><FiHome size={20} /></div>
                                        <div>
                                            <strong>{i18n.t('menu_button')}</strong>
                                            <p>{i18n.t('help_nav_home_desc')}</p>
                                        </div>
                                    </div>

                                    <div className="help-nav-item">
                                        <div className="help-nav-icon"><FiUsers size={20} /></div>
                                        <div>
                                            <strong>{i18n.t('patient_button')}</strong>
                                            <p>{i18n.t('help_nav_patients_desc')}</p>
                                        </div>
                                    </div>

                                    <div className="help-nav-item">
                                        <div className="help-nav-icon"><FiDatabase size={20} /></div>
                                        <div>
                                            <strong>{i18n.t('assay_button')}</strong>
                                            <p>{i18n.t('help_nav_assays_desc')}</p>
                                            <p className="help-nav-tip">{i18n.t('help_nav_assays_tip')}</p>
                                        </div>
                                    </div>

                                    <div className="help-nav-item">
                                        <div className="help-nav-icon"><LuMicroscope size={20} /></div>
                                        <div>
                                            <strong>{i18n.t('biobanks_button')}</strong>
                                            <p>{i18n.t('help_nav_biobanks_desc')}</p>
                                        </div>
                                    </div>

                                    <div className="help-nav-item">
                                        <div className="help-nav-icon"><FiShoppingCart size={20} /></div>
                                        <div>
                                            <strong>{i18n.t('order_button')}</strong>
                                            <p>{i18n.t('help_nav_order_desc')}</p>
                                        </div>
                                    </div>

                                    <div className="help-nav-item">
                                        <div className="help-nav-icon"><FiFileText size={20} /></div>
                                        <div>
                                            <strong>{i18n.t('documents_title')}</strong>
                                            <p>{i18n.t('help_nav_docs_desc')}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'filters' && (
                                <div className="help-section">
                                    <h3>{i18n.t('help_filters_title')}</h3>
                                    <p>{i18n.t('help_filters_intro')}</p>

                                    <h4>{i18n.t('help_filter_sex_title')}</h4>
                                    <p>{i18n.t('help_filter_sex_desc')}</p>

                                    <h4>{i18n.t('help_filter_age_title')}</h4>
                                    <p>{i18n.t('help_filter_age_desc')}</p>

                                    <h4>{i18n.t('help_filter_org_title')}</h4>
                                    <p>{i18n.t('help_filter_org_desc')}</p>

                                    <h4>{i18n.t('help_filter_collection_title')}</h4>
                                    <p>{i18n.t('help_filter_collection_desc')}</p>

                                    <h4>{i18n.t('help_filter_type_title')}</h4>
                                    <p>{i18n.t('help_filter_type_desc')}</p>

                                    <h4>{i18n.t('help_actions_title')}</h4>
                                    <ul>
                                        <li><strong>{i18n.t('request_button')}</strong> - {i18n.t('help_apply_desc')}</li>
                                        <li><strong>{i18n.t('select_all_button')}/{i18n.t('clear_all_button')}</strong> - {i18n.t('help_reset_desc')}</li>
                                        <li><strong>{i18n.t('make_request')}</strong> - {i18n.t('help_request_desc')}</li>
                                    </ul>
                                </div>
                            )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HelpModal;
